import fetchWithAuth from './fetchWithAuth';

const API = process.env.REACT_APP_BACKEND_URL;

const getToken = () => localStorage.getItem('access_token');
const authHeaders = () => ({
  Authorization: `Bearer ${getToken()}`,
  'Content-Type': 'application/json',
});

export const rmLibraryService = {
  /**
   * Fetch sections with slim projection + optional counts in a single call.
   * Supports infinite scroll (skip_count=true for subsequent pages).
   */
  async listSections({ page = 1, pageSize = 50, search, profileType, materialType, grade, quality, sortBy, sortDir, includeCounts = false, skipCount = false, columnFilters, signal } = {}) {
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('page_size', pageSize);
    params.set('view', 'table');
    if (search) params.set('search', search);
    if (profileType) params.set('profile_type', profileType);
    if (materialType) params.set('material_type', materialType);
    if (grade) params.set('grade', grade);
    if (quality) params.set('quality', quality);
    if (sortBy) params.set('sort_by', sortBy);
    if (sortDir) params.set('sort_dir', sortDir);
    if (includeCounts) params.set('include_counts', 'true');
    if (skipCount) params.set('skip_count', 'true');
    if (columnFilters && Object.keys(columnFilters).length > 0) params.set('column_filters', JSON.stringify(columnFilters));
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/sections?${params}`, { headers: authHeaders(), signal });
    if (!res.ok) throw new Error('Failed to fetch sections');
    return res.json();
  },

  async getProfileCounts({ materialType, grade, quality, signal } = {}) {
    const params = new URLSearchParams();
    if (materialType) params.set('material_type', materialType);
    if (grade) params.set('grade', grade);
    if (quality) params.set('quality', quality);
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/profile-counts?${params}`, { headers: authHeaders(), signal });
    if (!res.ok) throw new Error('Failed to fetch profile counts');
    return res.json();
  },

  async getStats() {
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/stats`, { headers: authHeaders() });
    if (!res.ok) throw new Error('Failed to fetch stats');
    return res.json();
  },

  async addSection(data) {
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/sections`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to add section');
    }
    return res.json();
  },

  async batchCreateSections(data) {
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/sections/batch`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Batch creation failed');
    }
    return res.json();
  },

  async updateSection(profileCode, updates) {
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/sections/${encodeURIComponent(profileCode)}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(updates),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to update section');
    }
    return res.json();
  },

  async getPipeNbOdMap() {
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/ref/pipe-nb-od`, { headers: authHeaders() });
    if (!res.ok) throw new Error('Failed to fetch pipe NB-OD map');
    return res.json();
  },

  async getPipeScheduleWt(nbMm) {
    const params = nbMm ? `?nb_mm=${nbMm}` : '';
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/ref/pipe-schedule-wt${params}`, { headers: authHeaders() });
    if (!res.ok) throw new Error('Failed to fetch pipe schedule data');
    return res.json();
  },

  async getSchemas() {
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/schemas`, { headers: authHeaders() });
    if (!res.ok) throw new Error('Failed to fetch profile schemas');
    return res.json();
  },

  async updateSchema(profileId, updates) {
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/schemas/${encodeURIComponent(profileId)}`, {
      method: 'PUT', headers: authHeaders(), body: JSON.stringify(updates),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed to update schema'); }
    return res.json();
  },

  async createSchema(data) {
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/schemas`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify(data),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed to create profile'); }
    return res.json();
  },

  async deleteSchema(profileId) {
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/schemas/${encodeURIComponent(profileId)}`, {
      method: 'DELETE', headers: authHeaders(),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed to delete profile'); }
    return res.json();
  },

  async getStandardSizes(profileType) {
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/ref/standard-sizes?profile_type=${encodeURIComponent(profileType)}`, { headers: authHeaders() });
    if (!res.ok) return [];
    return res.json();
  },

  async bulkStatusUpdate(profileCodes, status) {
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/bulk-status`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ profile_codes: profileCodes, status }),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Bulk status update failed'); }
    return res.json();
  },

  async bulkGradeUpdate(profileCodes, materialGrade) {
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/bulk-grade`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ profile_codes: profileCodes, material_grade: materialGrade }),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Bulk grade update failed'); }
    return res.json();
  },

  async createGrade(data) {
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/grades`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed to create grade'); }
    return res.json();
  },

  async getStandards() {
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/ref/standards`, { headers: authHeaders() });
    if (!res.ok) throw new Error('Failed to fetch standards');
    return res.json();
  },

  async createStandard(data) {
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/ref/standards`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed to create standard'); }
    return res.json();
  },

  async deleteStandard(code) {
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/ref/standards/${encodeURIComponent(code)}`, {
      method: 'DELETE', headers: authHeaders(),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed to delete standard'); }
    return res.json();
  },

  async getGrades() {
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/grades`, { headers: authHeaders() });
    if (!res.ok) throw new Error('Failed to fetch grades');
    return res.json();
  },

  async getMaterials() {
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/materials`, { headers: authHeaders() });
    if (!res.ok) throw new Error('Failed to fetch materials');
    return res.json();
  },

  async createMaterial(data) {
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/materials`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed to create material'); }
    return res.json();
  },

  async deleteMaterial(id) {
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/materials/${encodeURIComponent(id)}`, {
      method: 'DELETE', headers: authHeaders(),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed to delete material'); }
    return res.json();
  },

  async getColumnValues({ field, search, profileType, materialType, grade, quality, availability, columnFilters, signal } = {}) {
    const params = new URLSearchParams();
    params.set('field', field);
    if (search) params.set('search', search);
    if (profileType) params.set('profile_type', profileType);
    if (materialType) params.set('material_type', materialType);
    if (grade) params.set('grade', grade);
    if (quality) params.set('quality', quality);
    if (availability) params.set('availability', availability);
    if (columnFilters) params.set('column_filters', JSON.stringify(columnFilters));
    const res = await fetchWithAuth(`${API}/api/v1/rm-library/column-values?${params}`, { headers: authHeaders(), signal });
    if (!res.ok) return { field, values: [] };
    return res.json();
  },
};
